<template>
    <Teleport to="body">
        <transition name="fade">
            <div
                v-if="isOpen"
                class="fixed inset-0 z-50 bg-white dark:bg-slate-800 flex flex-col"
                role="dialog"
                aria-labelledby="insights-display-modal-title"
            >
                <header
                    class="flex items-center justify-between px-6 pt-6 pb-4"
                >
                    <h2
                        id="insights-display-modal-title"
                        class="text-xl font-medium text-slate-800 dark:text-slate-100"
                    >
                        Display settings
                    </h2>
                    <button
                        type="button"
                        class="btn-base btn-icon !m-0"
                        aria-label="Close"
                        @click="handleCancel"
                    >
                        <Icon icon="x" class="text-slate-500 dark:text-slate-400" />
                    </button>
                </header>

                <div
                    class="flex-1 self-stretch overflow-y-auto flex flex-col items-start gap-6 py-4 px-6"
                >
                    <section class="self-stretch">
                        <h3
                            class="text-base font-medium text-slate-800 dark:text-slate-100 mb-4"
                        >
                            Group by
                        </h3>
                        <div class="flex flex-col gap-3">
                            <label
                                v-for="option in groupByOptions"
                                :key="option.value"
                                class="flex items-center gap-2 cursor-pointer group"
                                @click="selectGroupBy(option.value)"
                            >
                                <div class="relative flex items-center">
                                    <input
                                        type="radio"
                                        name="insightsGroupBy"
                                        :value="option.value"
                                        class="absolute opacity-0"
                                        :checked="
                                            insightsGroupBy === option.value
                                        "
                                    />
                                    <span
                                        :class="[
                                            insightsGroupBy === option.value
                                                ? 'bg-10x-orange'
                                                : 'bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600',
                                            'w-4 h-4 rounded-full flex items-center justify-center',
                                        ]"
                                    >
                                        <span
                                            v-if="
                                                insightsGroupBy === option.value
                                            "
                                            class="w-1.5 h-1.5 bg-white rounded-full"
                                        ></span>
                                    </span>
                                </div>
                                <span
                                    class="text-base text-slate-800 group-hover:text-slate-900 dark:text-slate-100 dark:group-hover:text-white"
                                >
                                    {{ option.label }}
                                </span>
                            </label>
                        </div>
                    </section>

                    <div class="self-stretch h-px bg-slate-200 dark:bg-slate-700"></div>

                    <section class="self-stretch">
                        <h3
                            class="text-base font-medium text-slate-800 dark:text-slate-100 mb-4"
                        >
                            Benefit list options
                        </h3>
                        <div class="flex items-center gap-3">
                            <Switch
                                :value="insightsShowCardNumbers"
                                @input="toggleShowCardNumbers"
                            />
                            <span class="text-base text-slate-800 dark:text-slate-100">
                                Show cards #'s
                            </span>
                        </div>
                    </section>
                </div>

                <footer class="px-6 py-4 border-t border-slate-100 dark:border-slate-700">
                    <button
                        class="w-full px-4 py-2.5 bg-slate-900 text-white rounded shadow text-sm font-medium hover:bg-slate-800 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-2"
                        @click="handleApply"
                    >
                        Apply
                    </button>
                </footer>
            </div>
        </transition>
    </Teleport>
</template>

<script setup>
import { ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useCardBenefitsStore } from '@/store/card-benefits';
import Icon from '@/components/ui/icon/icon.vue';
import Switch from '@/components/ui/switch/switch.vue';

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['close']);

const store = useCardBenefitsStore();
const { insightsGroupBy, insightsShowCardNumbers } = storeToRefs(store);

const originalValues = ref({
    insightsGroupBy: 'flat',
    insightsShowCardNumbers: true,
});

watch(
    () => props.isOpen,
    (isOpen) => {
        if (isOpen) {
            originalValues.value = {
                insightsGroupBy: insightsGroupBy.value,
                insightsShowCardNumbers: insightsShowCardNumbers.value,
            };
        }
    }
);

const groupByOptions = [
    { value: 'flat', label: 'None' },
    { value: 'by_card', label: 'Card' },
];

const toggleShowCardNumbers = () => {
    store.setInsightsShowCardNumbers(!insightsShowCardNumbers.value);
};

const selectGroupBy = (value) => {
    store.setInsightsGroupBy(value);
};

const handleCancel = () => {
    store.setInsightsShowCardNumbers(
        originalValues.value.insightsShowCardNumbers
    );
    store.setInsightsGroupBy(originalValues.value.insightsGroupBy);
    emit('close');
};

const handleApply = () => {
    store.setInsightsGroupBy(insightsGroupBy.value);
    emit('close');
};
</script>
